import { useEffect } from "react";

/**
 * useHideInvisibleAds
 * Detects ad containers with `invisible` or `display: none` markers
 * and hides their parent wrapper to remove empty space.
 */
export function useHideInvisibleAds(
  wrapperSelector = "[data-testid='advertisement-element']",
) {
  useEffect(() => {
    const hideInvisible = () => {
      const adWrappers = document.querySelectorAll(wrapperSelector);

      adWrappers.forEach((wrapper) => {
        const el = wrapper as HTMLElement;
        const parent = el.closest("[data-testid='ads-container']");
        if (!parent) return;

        const adunit = el.querySelector("[data-testid^='dfp-']");
        if (!adunit) return;
        // Check if wrapper or its child has invisible class or height is 0
        const hasInvisibleClass = el.className.includes("invisible");
        const isHidden = window.getComputedStyle(adunit).display === "none";

        // Hide wrapper if invisible/hidden, otherwise show it
        (parent as HTMLElement).style.display =
          hasInvisibleClass || isHidden ? "none" : "";
      });
    };

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleHide = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(hideInvisible, 3000);
    };

    // Initial check (after 3s)
    scheduleHide();

    // Observe DOM for new ads being injected
    const observer = new MutationObserver(scheduleHide);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [wrapperSelector]);
}
